<template>
  <b-modal
    v-model="isOpen"
    has-modal-card
    :can-cancel="['escape', 'x', 'outside']"
    trap-focus
  >
    <div class="modal-card invite-modal-card">
      <header class="modal-card-head">
        <h2 class="modal-card-title">Invite others to my team!</h2>
        <button class="delete" @click="close"></button>
      </header>
      <section class="modal-card-body invite-modal">
        <div class="search-section">
          <h3>Find teammates already on Teamwrkr</h3>
          <form class="search-form" @submit.prevent="searchRosterMembers">
            <b-field label="Search by name or email" :type="rosterSearchError ? 'is-danger' : null">
              <b-input
                v-model="rosterSearchTerm"
                placeholder="Start typing a teammate's name or email"
                :disabled="rosterSearchLoading"
              />
            </b-field>
            <b-button
              type="is-primary"
              native-type="submit"
              :loading="rosterSearchLoading"
              :disabled="!rosterSearchTerm.trim() || rosterSearchLoading"
            >
              Search
            </b-button>
          </form>
          <p v-if="!rosterSearchAttempted" class="search-hint">
            We'll look for coworkers already connected to your company.
          </p>
          <p v-if="rosterSearchError" class="search-state error">
            {{ rosterSearchError }}
          </p>
          <div v-else-if="rosterSearchLoading" class="search-state loading">
            <b-loading :is-full-page="false" :active="true" size="is-small" />
            <span>Searching...</span>
          </div>
          <div v-else-if="rosterSearchResults.length" class="search-results">
            <div
              v-for="member in rosterSearchResults"
              :key="member._id || member.email"
              :class="[
                'search-result',
                {
                  selected:
                    member._id &&
                    member.requestStatus !== 'pending' &&
                    selectedSearchMembers.includes(member._id),
                },
              ]"
            >
              <div class="result-select">
                <b-checkbox
                  v-if="!isPendingInvite(member)"
                  v-model="selectedSearchMembers"
                  :native-value="member._id"
                  :disabled="!member._id || rosterInviteLoading"
                />
                <span v-else class="pending-label">Request Pending</span>
              </div>
              <div class="result-details">
                <div class="result-name">
                  {{ member.firstName || '-' }} {{ member.lastName || '' }}
                </div>
                <div class="result-email">{{ member.email || 'Email unavailable' }}</div>
                <div v-if="member.title" class="result-meta">{{ member.title }}</div>
              </div>
            </div>
            <div v-if="selectedSearchMembers.length" class="search-actions">
              <b-button
                type="is-primary"
                :loading="rosterInviteLoading"
                :disabled="rosterInviteLoading"
                @click="inviteSelectedMembers"
              >
                Invite to my Team
              </b-button>
            </div>
            <p v-if="rosterInviteError" class="search-state error">
              {{ rosterInviteError }}
            </p>
            <p v-if="rosterInviteSuccess" class="search-state success">
              {{ rosterInviteSuccess }}
            </p>
          </div>
          <p v-else-if="rosterSearchAttempted" class="search-state empty">
            No teammates matched that search.
          </p>
        </div>

        <hr class="invite-divider" />

        <p>
          If you know someone who isn't already on Teamwrkr, share your invite link with them using the button below.
        </p>
        <div class="invite-actions">
          <b-button
            type="is-primary"
            :loading="inviteLinkLoading"
            :disabled="!inviteLink || inviteLinkLoading"
            @click="copyInviteLink"
          >
            Copy invite link to clipboard
          </b-button>
          <span v-if="copySuccess" class="copy-confirm">Copied!</span>
        </div>
        <p v-if="inviteLinkError" class="search-state error">{{ inviteLinkError }}</p>
        <p v-else-if="inviteLinkExpiryLabel" class="search-hint">
          This link expires on {{ inviteLinkExpiryLabel }}. New people who sign up from it join your company once your tenant owner or an admin approves, then your team. After it expires, people can still ask to join and you approve them.
        </p>
      </section>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'RosterInviteModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    inviteLink: {
      type: String,
      default: '',
    },
    inviteLinkLoading: {
      type: Boolean,
      default: false,
    },
    inviteLinkError: {
      type: String,
      default: '',
    },
    inviteLinkExpiresAt: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      copySuccess: false,
      rosterSearchTerm: '',
      rosterSearchResults: [],
      rosterSearchLoading: false,
      rosterSearchError: null,
      rosterSearchAttempted: false,
      selectedSearchMembers: [],
      rosterInviteLoading: false,
      rosterInviteError: null,
      rosterInviteSuccess: null,
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    config() {
      return {
        headers: { Authorization: this.$cookies.get('teamwrkr-token') },
      }
    },
    inviteLinkExpiryLabel() {
      if (!this.inviteLinkExpiresAt) return ''
      const expires = new Date(this.inviteLinkExpiresAt)
      return Number.isNaN(expires.getTime()) ? '' : expires.toLocaleDateString()
    },
  },
  watch: {
    modelValue(value) {
      if (value) {
        this.resetRosterSearch()
      } else {
        this.copySuccess = false
      }
    },
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false)
    },
    async copyInviteLink() {
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(this.inviteLink)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = this.inviteLink
          textarea.setAttribute('readonly', '')
          textarea.style.position = 'absolute'
          textarea.style.left = '-9999px'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        this.copySuccess = true
        setTimeout(() => (this.copySuccess = false), 2000)
      } catch (error) {
        console.error('Unable to copy team invite link:', error)
      }
    },
    resetRosterSearch() {
      this.rosterSearchTerm = ''
      this.rosterSearchResults = []
      this.rosterSearchLoading = false
      this.rosterSearchError = null
      this.rosterSearchAttempted = false
      this.selectedSearchMembers = []
      this.rosterInviteLoading = false
      this.rosterInviteError = null
      this.rosterInviteSuccess = null
    },
    async searchRosterMembers() {
      const query = this.rosterSearchTerm.trim()
      if (!query) {
        this.rosterSearchError = 'Enter a teammate name or email to search.'
        return
      }

      this.rosterSearchLoading = true
      this.rosterSearchError = null
      this.rosterSearchResults = []
      this.rosterSearchAttempted = false
      this.selectedSearchMembers = []
      this.rosterInviteError = null
      this.rosterInviteSuccess = null

      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/company/search`,
          { term: query },
          this.config,
        )
        this.rosterSearchResults = Array.isArray(response.data) ? response.data : []
        this.rosterSearchAttempted = true
      } catch (error) {
        console.error('Error searching team:', error)
        this.rosterSearchError = 'Unable to search right now. Please try again shortly.'
      } finally {
        this.rosterSearchLoading = false
      }
    },
    async inviteSelectedMembers() {
      const targetIds = this.selectedSearchMembers.filter(Boolean)
      if (!targetIds.length) return

      this.rosterInviteLoading = true
      this.rosterInviteError = null
      this.rosterInviteSuccess = null

      try {
        await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/roster/invite`,
          { userIds: targetIds },
          this.config,
        )

        const inviteCount = targetIds.length
        this.rosterInviteSuccess =
          inviteCount === 1
            ? 'Invite sent to the selected teammate.'
            : `Invites sent to ${inviteCount} teammates.`
        this.selectedSearchMembers = []
      } catch (error) {
        console.error('Error inviting team members:', error)
        this.rosterInviteError = 'Unable to send invites right now. Please try again later.'
      } finally {
        this.rosterInviteLoading = false
      }
    },
    isPendingInvite(member) {
      return member?.requestStatus === 'pending'
    },
  },
}
</script>

<style lang="scss" scoped>
.invite-modal-card {
  max-width: 520px;
  width: 100%;
}

.invite-modal {
  padding: $space-5;
  text-align: left;

  h2 {
    font-size: $font-size-lg;
    margin-bottom: $space-2;
  }

  p {
    margin-bottom: $space-4;
    color: $color-text-muted;
  }
}

.search-section {
  margin-bottom: $space-5;
}

.search-form {
  display: flex;
  flex-wrap: wrap;
  gap: $space-3;
  align-items: flex-end;

  .field {
    flex: 1;
    margin-bottom: 0;
  }
}

.search-hint {
  margin-top: $space-2;
  color: $color-text-subtle;
}

.search-state {
  display: flex;
  align-items: center;
  gap: $space-2;
  margin-top: $space-2;
  color: $color-text-muted;

  &.error {
    color: $color-danger;
  }

  &.empty {
    color: $color-text-subtle;
  }

  &.success {
    color: $color-success;
  }
}

.search-results {
  @include flex-column($space-3);
  margin-top: $space-4;
}

.search-result {
  display: flex;
  gap: $space-3;
  align-items: flex-start;
  padding: $space-3;
  border: 1px solid $color-border-muted;
  border-radius: $radius-md;
  background: $color-surface-soft;

  &.selected {
    border-color: $color-primary-dark;
    background: $color-primary-soft;
  }
}

.result-select {
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;

  .b-checkbox.checkbox {
    margin-top: $space-1;
  }
}

.pending-label {
  font-weight: 600;
  color: $color-warning;
  font-size: $font-size-sm;
}

.result-details {
  flex: 1;
}

.result-name {
  font-weight: 600;
  color: $color-primary-dark;
}

.result-email,
.result-meta {
  font-size: $font-size-sm;
  color: $color-text-muted;
}

.search-actions {
  margin-top: $space-4;
  display: flex;
  justify-content: flex-end;
}

.invite-divider {
  margin: $space-4 0 $space-5;
  border: none;
  border-top: 1px solid $color-border;
}

.invite-actions {
  display: flex;
  align-items: center;
  gap: $space-3;
}

.copy-confirm {
  color: $color-success;
  font-weight: 600;
}
</style>
